import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@crowdex/ui/components/tooltip';
import { TONES } from '@crowdex/ui/lib/tones';
import { RotateCw, TriangleAlert } from 'lucide-react';
import { readableErrorMessage } from '@/lib/error-message';

/**
 * A frame's error, over the frame. Placements range from a composer button to a
 * panel: where the notice doesn't fit it shrinks to a tinted warning chip, and
 * where it does it stays on one line. Hovering either shows the plugin, the
 * full failure and how to recover; the button restarts the plugin.
 */
export function PluginFrameError({
  name,
  message,
  onRestart,
}: {
  /** The plugin surface's title, so the notice says what failed. */
  name: string;
  message: string;
  onRestart(): void;
}) {
  const headline = `${name} stopped working.`;
  const detail = readableErrorMessage(message);
  return (
    <div
      className="@container absolute inset-0 z-10 flex items-center justify-center overflow-hidden bg-card @min-[16rem]:px-2"
      role="alert"
    >
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`flex max-h-full min-w-0 max-w-full items-center gap-2 rounded-md border @min-[16rem]:w-full @min-[16rem]:max-w-md @min-[16rem]:rounded-lg @min-[16rem]:py-1 @min-[16rem]:pr-1 @min-[16rem]:pl-2.5 ${TONES.error.card}`}
            >
              <p className="hidden min-w-0 flex-1 truncate text-sm @min-[16rem]:block">
                <span className="font-medium text-foreground">{headline}</span>{' '}
                <span className="text-muted-foreground">{detail}</span>
              </p>
              <button
                type="button"
                aria-label={`${headline} ${detail} Restart`}
                className={`flex size-7 max-h-full max-w-full shrink-0 items-center justify-center gap-1.5 rounded-md hover:bg-accent/60 @min-[16rem]:w-auto @min-[16rem]:border @min-[16rem]:border-hairline-strong @min-[16rem]:px-2 @min-[16rem]:text-foreground ${TONES.error.mark}`}
                onClick={onRestart}
              >
                <TriangleAlert className="size-4 shrink-0 @min-[16rem]:hidden" />
                <RotateCw className="hidden size-3.5 @min-[16rem]:block" />
                <span className="hidden text-xs @min-[16rem]:inline">Restart</span>
              </button>
            </div>
          </TooltipTrigger>
          <TooltipContent className="max-w-80">
            <p className="font-medium">{headline}</p>
            <p className="break-words text-muted-foreground">{detail}</p>
            <p className="mt-1 text-muted-foreground">Restart it to try again.</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
